import { useEffect, useRef } from 'react';
import styles from './InstagramEmbed.module.css';

interface SingleInstagramPostProps {
  postUrl: string;
  index: number;
}

function SingleInstagramPost({ postUrl, index }: SingleInstagramPostProps) {
  const blockquoteRef = useRef<HTMLQuoteElement>(null);

  useEffect(() => {
    // Process this specific embed when the component mounts
    const timer = setTimeout(() => {
      if (window.instgrm) {
        window.instgrm.Embeds.process(blockquoteRef.current?.parentElement);
      }
    }, 100 + index * 200); // Stagger processing

    return () => clearTimeout(timer);
  }, [postUrl, index]);

  return (
    <div className={styles.embedWrapper}>
      <blockquote
        ref={blockquoteRef}
        className="instagram-media"
        data-instgrm-captioned
        data-instgrm-permalink={postUrl}
        data-instgrm-version="14"
        style={{
          background: '#FFF',
          border: '0',
          borderRadius: '3px',
          boxShadow: '0 0 1px 0 rgba(0,0,0,0.5),0 1px 10px 0 rgba(0,0,0,0.15)',
          margin: '1px',
          maxWidth: '540px',
          minWidth: '326px',
          padding: '0',
          width: 'calc(100% - 2px)',
        }}
      >
        <a href={postUrl} target="_blank" rel="noopener noreferrer">
          Ver esta publicación en Instagram
        </a>
      </blockquote>
    </div>
  );
}

interface InstagramEmbedProps {
  posts: string[];
  title?: string;
}

export function InstagramEmbed({ posts, title = 'Algunas animaciones' }: InstagramEmbedProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Load Instagram embed script once
    const loadInstagramScript = () => {
      // Check if script already exists
      const existingScript = document.querySelector(
        'script[src="https://www.instagram.com/embed.js"]'
      );

      if (!existingScript) {
        const script = document.createElement('script');
        script.src = 'https://www.instagram.com/embed.js';
        script.async = true;
        document.body.appendChild(script);
      } else if (window.instgrm) {
        // Script exists, just process
        window.instgrm.Embeds.process();
      }
    };

    const timer = setTimeout(loadInstagramScript, 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className={styles.container} ref={containerRef}>
      <h3 className={styles.title}>{title}</h3>
      <div className={styles.grid}>
        {posts.map((postUrl, index) => (
          <SingleInstagramPost key={postUrl} postUrl={postUrl} index={index} />
        ))}
      </div>
    </div>
  );
}

// Extend window interface for Instagram
declare global {
  interface Window {
    instgrm?: {
      Embeds: {
        process: (element?: HTMLElement | null) => void;
      };
    };
  }
}
